import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactTable from "react-table";
import ReactTablePagination from "components/task-list/components/pagination";
import Modal from "components/modal";
import RepostModal from "./repost-modal";
import ShipmentStatus from "../../components/shipment-status";
import authContext from "contexts/auth/authContext";
import { Chip } from "../../components/chip/chip";

import { useNavigate } from "react-router-dom";
import { useNewSearch } from "../../shared/hooks/api/use-new-search";
import { useChangeTasksStatuses } from "../../shared/hooks/use-change-tasks-statuses";
import { useModal } from "../../shared/hooks/use-modal";
import { useUserAdvancedFilters } from "../../shared/hooks/use-user-advanced-filters";
import { usePagination } from "shared/hooks/use-pagination";
import { useLocations } from "components/search-advanced-filters/hooks/use-locations";
import { sendToIntake, undoToIntake } from "./lib/data";
import { buildColumns, hasInvalidProductStatus } from "./lib/constants";
import { Textarea } from "components/form-inputs/inputs";
import { Select } from "components/form-inputs/inputs/select";
import { SettingsContext } from "contexts/settings";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";
import { TASK_PERMISSIONS } from "shared/constants";
import * as notify from "shared/notify";

import { faClose, faEye, faFilter, faUndoAlt } from "@fortawesome/free-solid-svg-icons";
import { hasErrorResponse } from "../../shared/data";
import "./repost-styles.scss";
import { useShipmentStatuses } from "../../shared/hooks/api/use-shipment-statuses";
import Loader from "../../components/loader/loader";
import { searchAdvancedFiltersFields } from "../../components/task-list/lib/constants";
import { FORM_TYPE } from "../../components/task-list/components/form-selector";
import { AdvancedFilters } from "../../components/advanced-filters/advanced-filters";
import { AdvancedFiltersProvider } from "../../contexts/advanced-filters/advanced-filters-provider";
import { SHIPMENT_FILTER_TYPES } from "../../components/shipments/lib/constants";
import { BRAND_VERIFIED_WORKFLOW_TYPE_ID } from "../../components/shipment-editor/lib/constants";

const DEFAULT_REDO_PRODUCT = { key: "", shipmentKey: "", isGDNRepost: false };

const UpcSearchIndex = () => {
    const ADMIN_SEARCH_ADVANCED_FILTER = "admin-search-advanced-filter";
    const navigate = useNavigate();
    const { isAllowedPermission } = useContext(authContext);
    const { settings } = useContext(SettingsContext);
    const { viewOnlyAdmin } = useContext(ViewOnlyPermissionsContext);
    const [redoProduct, setRedoProduct] = useState({ ...DEFAULT_REDO_PRODUCT });
    const [upcs, setUpcs] = useState();
    const [upcList, setUpcList] = useState([]);
    const { externalFilters, prevExternalFilters, clearFilters, applyExternalFilters, clearAdvancedFilters, userFilters } =
        useUserAdvancedFilters(ADMIN_SEARCH_ADVANCED_FILTER);
    const { open: isAdvancedFiltersOpen, onOpen: handleOpenAdvancedFilters, onClose: handleCloseAdvancedFilters } = useModal();
    const { open: isShipmentStatusModalOpen, onOpen: handleOpenShipmentStatusModal, onClose: handleCloseShipmentStatusModal } = useModal();
    const { data: locations } = useLocations();
    const [
        { pageNumber, pageSize, sortBy, totalCount, totalPages, searchParams, pagingParams, filteredValues },
        { setPageSize, setPage, setSortBy, setTotalCount, search, searchByUpcs, resetFilters },
    ] = usePagination({
        callback: () => {
            setUpcs(undefined);
            setUpcList([]);
        },
    });
    const { data: shipmentsUpcs, mutate: searchShipmentUpcs, isLoading: isLoadingShipmentUpcs } = useShipmentStatuses();
    const { data: searchResult, mutate: searchProducts, isSuccess, isLoading } = useNewSearch("admin");
    const checkPermission = isAllowedPermission(TASK_PERMISSIONS.accessPackageHierarchy);
    const [selectedRows, setSelectedRows] = useState([]);
    const { mutate: changeStatuses, isLoading: isProcessing } = useChangeTasksStatuses();

    const countCanCancelReturnToIntakeItems = selectedRows.filter(({ hasBeenFinalized, isFinalized }) => hasBeenFinalized && !isFinalized).length;
    const countCanReturnToIntakeItems = selectedRows.filter(
        ({ cesProductStatus, hasBeenFinalized, isFinalized }) => !hasInvalidProductStatus(cesProductStatus) && hasBeenFinalized && isFinalized
    ).length;
    const countInvalidProductStatusItems = selectedRows.filter(({ cesProductStatus }) => hasInvalidProductStatus(cesProductStatus)).length;
    const countIrrecoverableItems = selectedRows.filter(({ hasBeenFinalized, isFinalized }) => !hasBeenFinalized && !isFinalized).length;
    const shipments = shipmentsUpcs && shipmentsUpcs.items ? shipmentsUpcs.items : [];
    const selectedUpcs = selectedRows.map(({ upc, shipmentProduct }) => `${upc}${shipmentProduct.modifier ? `-${shipmentProduct.modifier}` : ""}`);
    const isBrandVerifiedSelected = selectedRows.some(({ workflowTypeId }) => workflowTypeId === BRAND_VERIFIED_WORKFLOW_TYPE_ID);
    const [groupKey, setGroupKey] = useState("");
    const groupByOptions = useMemo(
        () => [
            {
                text: "None",
                value: "",
            },
            {
                text: "UPC",
                value: "upc",
            },
            {
                text: "CES Client",
                value: "cxHClient",
            },
            {
                text: "GDN Client",
                value: "gdnClient",
            },
            {
                text: "Package Level",
                value: "packageLevelName",
            },
            {
                text: "Location",
                value: "locationName",
            },
            {
                text: "Bin ID",
                value: "binTrackingNumber",
            },
            {
                text: "Supplier",
                value: "supplier",
            },
            {
                text: "ISID",
                value: "internalSupplierId",
            },
            {
                text: "Shipment ID",
                value: "shipmentKey",
            },
            {
                text: "Updated",
                value: "updatedOn",
            },
        ],
        []
    );
    const optionsAssignments = {
        "shipment.type": SHIPMENT_FILTER_TYPES,
    };
    const isReturnToIntakeDisabled = useMemo(() => {
        return (
            isBrandVerifiedSelected ||
            countCanReturnToIntakeItems === 0 ||
            countCanCancelReturnToIntakeItems > 0 ||
            countInvalidProductStatusItems > 0 ||
            countIrrecoverableItems > 0 ||
            isProcessing ||
            viewOnlyAdmin.returnToIntake
        );
    }, [
        isBrandVerifiedSelected,
        countCanCancelReturnToIntakeItems,
        countCanReturnToIntakeItems,
        countInvalidProductStatusItems,
        countIrrecoverableItems,
        isProcessing,
        viewOnlyAdmin.returnToIntake,
    ]);
    const isCancelReturnToIntakeDisabled = useMemo(() => {
        return (
            countCanCancelReturnToIntakeItems === 0 ||
            countCanReturnToIntakeItems > 0 ||
            countInvalidProductStatusItems > 0 ||
            countIrrecoverableItems > 0 ||
            isProcessing ||
            viewOnlyAdmin.returnToIntake
        );
    }, [
        countCanCancelReturnToIntakeItems,
        countCanReturnToIntakeItems,
        countInvalidProductStatusItems,
        countIrrecoverableItems,
        isProcessing,
        viewOnlyAdmin.returnToIntake,
    ]);

    useEffect(() => {
        // apply/update advanced filters
        if (externalFilters) {
            search(externalFilters);

            return;
        }

        // if advanced filters were cleared, re-run search on normal filters with stored values
        if (prevExternalFilters && !externalFilters) {
            search(userFilters);
        }
    }, [externalFilters, prevExternalFilters]);

    useEffect(() => {
        // reset paging when some result where in the previous search
        if (searchParams && !searchParams.length && totalCount !== 0) {
            setUpcList([]);
            resetFilters();

            return;
        }

        if (searchParams.length > 0) {
            searchProducts({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });
        }
    }, [searchParams, pagingParams]);

    useEffect(() => {
        if (isSuccess) {
            setUpcList(searchResult.items);
            setTotalCount(searchResult.totalRecordCount);
            setSelectedRows([]);
        }
    }, [isSuccess, searchResult]);

    useEffect(() => {
        // don't trigger search when "upcs" is initialized
        if (upcs !== undefined) {
            searchByUpcs(upcs);
        }
    }, [upcs]);

    const closeModal = () => {
        setRedoProduct({ ...DEFAULT_REDO_PRODUCT });
    };

    const setFinalizeState = () => {
        notify.success("Product returned to Final QA");
        searchProducts({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });
        setSelectedRows([]);
    };

    const handleRepostIntake = async (key, shipmentKey, modalRepost, isSalesSample) => {
        const response = await sendToIntake(key, false);

        if (!response) {
            return;
        }

        if (!notify.errorResponse(response)) {
            setFinalizeState(key);
        } else {
            if (response && response.code === "MIGRATED_RECORD_OUT_OF_DATE") {
                const confirm = await notify.confirm(response.message);

                if (confirm) {
                    const forceResponse = await sendToIntake(key, true);

                    if (!forceResponse || notify.errorResponse(forceResponse)) {
                        return;
                    }

                    setFinalizeState(key);
                } else {
                    return;
                }
            }
        }

        if (modalRepost) {
            setRedoProduct({
                key,
                shipmentKey,
                isSalesSample,
            });
        }
    };

    const handleUndoRepostIntake = async (key) => {
        const response = await undoToIntake(key);

        if (!response) {
            return;
        }

        if (!response.success) {
            return notify.error("You do not have permission to perform this action.");
        }

        notify.success("Product removed from Final QA");
        searchProducts({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });
        setSelectedRows([]);
    };

    const handleRepostExternal = async (key, shipmentKey) => {
        setRedoProduct({
            key,
            shipmentKey,
            isExternalRepost: true,
        });
    };

    const handleLinkingManager = (row) => {
        if (!checkPermission) {
            return notify.error("You do not have permission to perform this action.");
        }

        navigate(`/admin/linking-manager/${row.value}`);
    };

    const handleResetFilters = () => {
        resetFilters();
        clearAdvancedFilters();
        setSelectedRows([]);
    };

    const handleUpdateSelectedRow = useCallback(
        (selectedRow, isRemove) => {
            if (isRemove === "true") {
                setSelectedRows(selectedRows.filter((row) => row.key !== selectedRow.key));
            } else {
                setSelectedRows([...selectedRows, selectedRow]);
            }
        },
        [selectedRows]
    );

    const handleSelectAllRows = useCallback((selections) => {
        setSelectedRows(selections);
    }, []);

    const handleChangeStatus = (status, message, changeData) => {
        const tasks = selectedRows.map(({ finalQaTaskKey }) => {
            return {
                taskKey: finalQaTaskKey,
                state: status,
                changeData: changeData,
            };
        });

        changeStatuses(
            { tasks, sourcePage: "admin", action: "returnToIntake" },
            {
                onSuccess: (res) => {
                    setSelectedRows([]);
                    searchProducts({ searchParams, pagingParams, isUsingExternalFilters: !!externalFilters });

                    if (hasErrorResponse(res)) {
                        return;
                    }

                    notify.success(message);
                },
            }
        );
    };

    const handleLoadUpcStatusesModal = () => {
        handleOpenShipmentStatusModal();
        searchShipmentUpcs({ shipmentKeys: selectedRows.map(({ shipment }) => shipment.key) });
    };

    const handleClearAdvancedFilters = () => {
        clearAdvancedFilters();
        setSelectedRows([]);
    };

    return (
        <div>
            {isAdvancedFiltersOpen && (
                <AdvancedFiltersProvider
                    optionsAssignments={optionsAssignments}
                    filtersModel={searchAdvancedFiltersFields}
                    onClose={handleCloseAdvancedFilters}
                    onFiltersSubmit={(filters) => applyExternalFilters(filters, false, handleCloseAdvancedFilters)}
                    storedFilters={userFilters}
                    clearFilters={clearFilters}
                    task={FORM_TYPE.AdminSearch}
                >
                    <AdvancedFilters open={isAdvancedFiltersOpen} />
                </AdvancedFiltersProvider>
            )}
            <h1>Task Search</h1>

            <div className="flex justify-content-between align-items-end m-b-12">
                <div className="flex border">
                    <Chip
                        label="Shipment Status"
                        icon={faEye}
                        disabled={selectedRows.length === 0}
                        onClick={handleLoadUpcStatusesModal}
                    />
                    <Chip
                        label="Return to Intake"
                        icon={faUndoAlt}
                        disabled={isReturnToIntakeDisabled}
                        onClick={() => handleChangeStatus("redo", `${selectedRows.length} Products returned to Final QA`, {})}
                    />
                    <Chip
                        label="Cancel Return to Intake"
                        icon={faClose}
                        disabled={isCancelReturnToIntakeDisabled}
                        onClick={() =>
                            handleChangeStatus("completed", `${selectedRows.length} Products removed from Final QA`, { isCancelRedo: true })
                        }
                    />
                </div>

                <div className="flex align-items-end">
                    <div>
                        <h3 className={`${!!externalFilters ? "disabled" : ""}`}>Filter By UPCs</h3>
                        <Textarea
                            className="-no-margin"
                            disabled={!!externalFilters}
                            name="upc"
                            value={upcs}
                            updateOnChange={(id, value) => setUpcs(value)}
                            disableResize={!!externalFilters}
                        />
                        <div
                            className="select-group"
                            style={{ display: "none" }}
                        >
                            <span>Group by:</span>
                            <Select
                                onChange={(event) => setGroupKey(event.target.value)}
                                value={groupKey}
                                excludeSelect={true}
                                id="select-all-filter"
                                options={groupByOptions}
                            />
                        </div>
                    </div>
                    <div className="m-l-12 m-r-12">
                        <Chip
                            disabled={isLoading}
                            label="Advanced Filters"
                            icon={faFilter}
                            onClick={handleOpenAdvancedFilters}
                            onClear={handleClearAdvancedFilters}
                            value={externalFilters && externalFilters.length}
                        />
                    </div>
                    <button
                        className="button -no-margin"
                        onClick={handleResetFilters}
                    >
                        Reset filters
                    </button>
                </div>
            </div>

            <ReactTable
                className="unset-overflow"
                style={{ overflow: "unset" }}
                manual
                data={upcList}
                filtered={filteredValues}
                // pivotBy={groupKey ? [groupKey] : []}
                sortable
                columns={buildColumns(
                    selectedRows,
                    locations,
                    handleUpdateSelectedRow,
                    handleSelectAllRows,
                    handleRepostIntake,
                    handleUndoRepostIntake,
                    handleRepostExternal,
                    handleLinkingManager,
                    settings.taskSettings.minimumTaskPublishDateForContextsQueryDate,
                    !externalFilters,
                    viewOnlyAdmin.returnToIntake,
                    viewOnlyAdmin.accessPackageLinkingTool
                )}
                minRows={upcList.length > 0 ? 1 : 3}
                totalRecords={totalCount}
                showPagination
                PaginationComponent={ReactTablePagination}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                onSortedChange={setSortBy}
                onFilteredChange={search}
                sorted={sortBy}
                page={pageNumber}
                pageSize={pageSize}
                pages={totalPages}
                loading={isLoading || isProcessing}
            />
            <Modal
                show={!!redoProduct.key}
                onClose={closeModal}
            >
                <RepostModal
                    isExternalRepost={redoProduct.isExternalRepost}
                    taskContextKey={redoProduct.key}
                    closeModal={closeModal}
                    disableWithUpdate={viewOnlyAdmin.resubmitWithUpdate}
                    disableWithoutUpdate={viewOnlyAdmin.resubmitWithoutUpdate}
                    isSalesSample={redoProduct.isSalesSample}
                />
            </Modal>
            <Modal
                cssClass="full-width"
                show={isShipmentStatusModalOpen}
                onClose={() => handleCloseShipmentStatusModal()}
            >
                {isLoadingShipmentUpcs ? (
                    <Loader />
                ) : (
                    <ShipmentStatus
                        products={shipments}
                        openFullUpcs={selectedUpcs}
                        showSubLabel
                    />
                )}
            </Modal>
        </div>
    );
};

export default UpcSearchIndex;
